/**
 * LE COURRIER DE RELANCE, au style des documents Stafa — lot 156, 03/11/2026.
 *
 * Choix de Mohamed : « les documents Stafa », c'est-à-dire la charte de la
 * confirmation de prise en charge (`EcranConfirmation.tsx`, classes `conf-*`
 * de `styles.css`). Les mêmes classes sont reprises ici : un seul dessin pour
 * les deux documents, et la feuille A4 de 794 × 1123 px que le PDF photographie.
 *
 * Les coordonnées du recouvrement, l'IBAN et le BIC ont été donnés par Mohamed
 * le 03/11 ; il a choisi de les écrire dans le code.
 */

import { forwardRef } from 'react';
import { LogoTns } from './LogoTns.js';
import {
  dateCourte,
  dateDite,
  montantDit,
  NOMS_NIVEAUX,
  texteDuCourrier,
  type LigneRelance,
  type NiveauPl,
} from './relancesPl.js';

/** L'émetteur, recopié de la confirmation (`EcranConfirmation.tsx`, constante `TNS`). */
const TNS = {
  rs: 'TNS LOGISTIQUE',
  l1: '2 route de Gasville — 28630 Nogent-le-Phaye',
  l2: 'SARL au capital de 17 048 € — RCS Chartres 910 696 004',
  l3: 'SIRET 910 696 004 00027 — NAF 4941A',
  l4: 'Identifiant TVA : FR89 910 696 004',
};

/** Le recouvrement — donné par Mohamed le 03/11/2026. */
export const RECOUVREMENT = {
  service: 'Service comptabilité',
  mail: 'facturation.client@tns-logistique.com',
  tel: 'Tél. : +33 2 34 40 33 15',
  iban: 'FR76 1027 8371 4000 0122 1450 129',
  bic: 'CMCIFR2A',
};

export interface ProprietesCourrier {
  readonly ligne: LigneRelance;
  readonly niveau: NiveauPl;
  readonly jour: string;
  readonly interets: number;
  readonly indemnite: number;
  readonly montantDu: number;
}

export const CourrierRelance = forwardRef<HTMLDivElement, ProprietesCourrier>(function CourrierRelance(
  { ligne, niveau, jour, interets, indemnite, montantDu },
  ref,
) {
  const client = ligne.client;
  const reste = Number(ligne.reste_du);
  const ttc = Number(ligne.montant_ttc);
  const paragraphes = texteDuCourrier(niveau, {
    echeance: ligne.echeance,
    envoyes: ligne.envoyes,
    jour,
    montantDu,
  });
  const siren = client?.siret == null ? null : client.siret.slice(0, 9);
  const numero = ligne.numero ?? '—';
  const retard = jour > ligne.echeance ? `${String(ligne.retard_jours)} j` : 'à échoir';

  return (
    <div className="conf-feuille courrier-relance" ref={ref}>
      <div className="conf-corps">
        <div className="conf-haut">
          <div className="conf-logo">
            <LogoTns />
          </div>
          <div className="conf-emetteur">
            <div className="conf-rs">{TNS.rs}</div>
            {TNS.l1}
            <br />
            {TNS.l2}
            <br />
            {TNS.l3}
            <br />
            {TNS.l4}
          </div>
        </div>

        <div className="conf-titre">
          <div className="conf-t">{NOMS_NIVEAUX[niveau]}</div>
        </div>

        <div className="conf-milieu">
          <div className="conf-renseignement">
            <div className="conf-souligne">Pour tout renseignement :</div>
            {RECOUVREMENT.service}
            <br />
            {RECOUVREMENT.mail}
            <br />
            {RECOUVREMENT.tel}
          </div>
          <div className="conf-client">
            <div className="conf-boite">
              <div className="conf-nom">{client?.nom ?? ligne.libelle ?? 'Client'}</div>
              {client?.code != null && <div className="conf-cpt">Compte {client.code}</div>}
              <div className="conf-adr">
                {client?.adresse ?? ''}
                <br />
                {client?.code_postal ?? ''} {client?.ville ?? ''}
                <br />
                {client?.pays ?? ''}
              </div>
            </div>
            <div className="conf-sous">
              <span>{siren === null ? '' : `SIREN ${siren}`}</span>
              <span>Page 1/1</span>
            </div>
          </div>
        </div>

        <table className="conf-bandeau">
          <tbody>
            <tr>
              <td>
                <div className="conf-quoi">Courrier du :</div>
                <div className="conf-val">{dateDite(jour)}</div>
              </td>
              <td>
                <div className="conf-quoi">Compte client :</div>
                <div className="conf-val">{client?.code ?? '—'}</div>
              </td>
              <td>
                <div className="conf-quoi">Facture :</div>
                <div className="conf-val">{numero}</div>
              </td>
              <td>
                <div className="conf-quoi">Montant dû :</div>
                <div className="conf-val">{montantDit(montantDu)} EUR</div>
              </td>
            </tr>
          </tbody>
        </table>

        <div className="conf-lettre">
          <p>Madame, Monsieur,</p>
          {paragraphes.map((t) => (
            <p key={t.slice(0, 40)}>{t}</p>
          ))}
          <p>Veuillez agréer, Madame, Monsieur, nos salutations distinguées.</p>
          <p className="conf-signature">Le service comptabilité — TNS Logistique</p>
        </div>

        <table className="conf-lignes">
          <thead>
            <tr>
              <th style={{ width: '20%' }}>Facture</th>
              <th style={{ width: '14%' }}>Date</th>
              <th style={{ width: '14%' }}>Échéance</th>
              <th className="conf-n" style={{ width: '14%' }}>
                Retard
              </th>
              <th className="conf-n" style={{ width: '19%' }}>
                Montant TTC
              </th>
              <th className="conf-n" style={{ width: '19%' }}>
                Reste dû
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <b>{numero}</b>
              </td>
              <td>{ligne.date_piece === null ? '—' : dateCourte(ligne.date_piece)}</td>
              <td>{dateCourte(ligne.echeance)}</td>
              <td className="conf-n">{retard}</td>
              <td className="conf-n">{montantDit(ttc)}</td>
              <td className="conf-n">{montantDit(reste)}</td>
            </tr>
          </tbody>
        </table>

        <div className="conf-pied">
          <div />
          <div className="conf-totaux">
            {indemnite > 0 && (
              <>
                <div className="conf-l">
                  <span>Factures</span>
                  <span>{montantDit(reste)}</span>
                </div>
                <div className="conf-l">
                  <span>Intérêts de retard</span>
                  <span>+ {montantDit(interets)}</span>
                </div>
                <div className="conf-l">
                  <span>Indemnité forfaitaire</span>
                  <span>+ {montantDit(indemnite)}</span>
                </div>
              </>
            )}
            <div className="conf-l conf-net">
              <span>MONTANT DÛ</span>
              <span>{montantDit(montantDu)} EUR</span>
            </div>
          </div>
        </div>
      </div>

      <div className="conf-talon">
        <div>
          <div className="conf-titre-talon">Règlement par virement</div>
          <div>
            IBAN {RECOUVREMENT.iban} — BIC {RECOUVREMENT.bic}
          </div>
          <div>Référence à rappeler : {numero}</div>
        </div>
        <div>
          <div className="conf-titre-talon">
            Facture {numero}
            {client?.code == null ? '' : ` — Compte ${client.code}`}
          </div>
          <div className="conf-gros">
            {montantDit(montantDu)} EUR <span className="conf-maigre">à régler</span>
          </div>
        </div>
      </div>

      <div className="conf-bande" />
    </div>
  );
});
